import React, { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useLoginAsAdmin } from "../../Query/useAuth";
import { clearStorage } from "../../Helpers/clearStorage";
import ConfirmationDialog from "../Common/ConfirmationDialog";
import {
  BadgeOutlined,
  AccessTimeOutlined,
  HistoryOutlined,
  SellOutlined,
  TaskAltOutlined,
  ArrowForwardRounded,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  IconButton,
  Stack,
  TextField,
  Typography,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import type { TicketPriority, TicketStatus } from "../../Models/helpDesk";
import {
  useSingleTicket,
  useUpdateTicket,
  useDeleteTicket,
  useHelpDeskAccess,
} from "../../Query/useHelpDesk";
import color from "../../Constants/colors";
import AttachmentList from "./AttachmentList";
import TicketConversation from "./TicketConversation";
type Props = {
  ticketId?: number;
  onClose: () => void;
  canManage: boolean;
};
const fieldSx = {
  "& .MuiOutlinedInput-root": {
    bgcolor: `${color.white} !important`,

    borderRadius: "8px",

    color: `${color.primary_dark} !important`,

    fontSize: "12px",

    "&:not(.MuiInputBase-multiline)": {
      height: "38px",
    },
  },

  "& .MuiInputBase-input, & textarea": {
    color: `${color.primary_dark} !important`,

    WebkitTextFillColor: `${color.primary_dark} !important`,

    fontSize: "12px",
  },

  "& .MuiInputLabel-root": {
    color: `${color.gray500} !important`,

    fontSize: "12px",

    transform: "translate(14px, 11px) scale(1)",
  },

  "& .MuiInputLabel-shrink": {
    transform: "translate(14px, -9px) scale(0.75)",
  },

  "&:has(.MuiInputBase-multiline) .MuiInputLabel-root:not(.MuiInputLabel-shrink)":
    {
      transform: "translate(14px, 16px) scale(1)",
    },

  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: `${color.gray200} !important`,
  },

  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: `${color.accent} !important`,
  },

  "& .MuiFormHelperText-root": {
    fontSize: "10px",

    mt: 0.5,
  },

  "& .MuiSelect-select": {
    fontSize: "12px",

    display: "flex",

    alignItems: "center",
  },

  "& .MuiSelect-icon": {
    color: color.primary_dark,

    fontSize: "18px",
  },
};

const AdminTicketEditModal: React.FC<Props> = ({
  ticketId,
  onClose,
  canManage,
}) => {
  const { t, i18n } = useTranslation("helpDesk");
  const {
    data: ticket,
    isLoading,
    isError,
    refetch,
  } = useSingleTicket(ticketId);
  const { data: access } = useHelpDeskAccess();
  const updateMutation = useUpdateTicket();
  const deleteMutation = useDeleteTicket();
  const loginAsUserMutation = useLoginAsAdmin();
  const queryClient = useQueryClient();
  const [confirmLoginAsUser, setConfirmLoginAsUser] = useState(false);
  const creator = ticket?.created_by;
  const ticketUserId = creator?.id;
  const creatorFullName =
    [creator?.name, creator?.surname].filter(Boolean).join(" ") ||
    creator?.email;

  const handleLoginAsUser = () => {
    if (
      ticketUserId == null ||
      loginAsUserMutation.isPending ||
      !access?.is_admin
    )
      return;
    loginAsUserMutation.mutate(String(ticketUserId), {
      onSuccess: async (data) => {
        if (!data?.cih_token) return;
        await clearStorage([
          "cw_conversation",
          "cw_clearence",
          "cih_token",
          "persist:annex:v1",
        ]);
        localStorage.setItem("cih_token", data.cih_token);
        queryClient.removeQueries();
        window.location.href = "/home";
      },
    });
  };

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState("");
  const [resolution, setResolution] = useState("");
  useEffect(() => {
    setTags(ticket?.tags ?? []);
    setTagInput("");
    setResolution(ticket?.resolution ?? "");
  }, [ticket?.id, ticket?.tags, ticket?.resolution]);
  const parseTags = (value: string) =>
    value
      .split(",")
      .map((x) => x.trim().replace(/^#/, ""))
      .filter(Boolean);
  const save = (updatedTicket: {
    status?: TicketStatus;
    priority?: TicketPriority;
    tags?: string[];
    resolution?: string;
  }) => {
    if (ticketId === undefined || !canManage || updateMutation.isPending)
      return;
    updateMutation.mutate(
      { id: ticketId, updatedTicket },
      { onSuccess: () => refetch() },
    );
  };
  const statusLabels: Record<TicketStatus, string> = {
    0: t("status.submitted"),
    1: t("status.inProgress"),
    2: t("status.closed"),
  };
  const priorityLabels: Record<TicketPriority, string> = {
    0: t("priority.low"),
    1: t("priority.normal"),
    2: t("priority.high"),
  };
  const statusStyles = [
    { bg: color.orange50, text: color.accent_dark },
    { bg: color.blue50, text: color.secondary },
    { bg: color.green50, text: color.green700 },
  ];
  const priorityStyles = [
    { bg: color.gray50, text: color.gray500 },
    { bg: color.blue50, text: color.secondary },
    { bg: color.red50, text: color.red700 },
  ];
  const statusStyle = ticket
    ? (statusStyles[ticket.status] ?? statusStyles[0])
    : statusStyles[0];
  const priorityStyle = ticket
    ? (priorityStyles[ticket.priority] ?? priorityStyles[1])
    : priorityStyles[1];
  const busy =
    updateMutation.isPending ||
    deleteMutation.isPending ||
    loginAsUserMutation.isPending;
  const close = () => {
    if (busy) return;
    setConfirmDelete(false);
    onClose();
  };
  const removeTicket = () => {
    if (ticketId === undefined) return;
    deleteMutation.mutate(ticketId, {
      onSuccess: () => {
        setConfirmDelete(false);
        onClose();
      },
    });
  };
  const labelSx = {
    color: `${color.gray500} !important`,
    fontSize: 12,
    fontWeight: 600,
  };
  const valueSx = {
    color: `${color.primary_dark} !important`,
    fontSize: 13,
    fontWeight: 700,
    overflowWrap: "anywhere",
  };
  const infoRow = (label: string, value: React.ReactNode) => (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "95px minmax(0, 1fr)",
          sm: "120px minmax(0, 1fr)",
        },
        gap: 1.5,
        alignItems: "center",
        py: 1.5,
      }}
    >
      <Typography sx={labelSx}>{label}</Typography>
      <Box sx={{ minWidth: 0 }}>{value}</Box>
    </Box>
  );
  return (
    <Dialog
      open={ticketId !== undefined}
      onClose={close}
      fullWidth
      maxWidth={false}
      PaperProps={{
        sx: {
          width: "min(1320px, 97vw)",
          maxWidth: "none",
          height: {
            xs: "96dvh",
            md: "min(880px, 94dvh)",
          },
          maxHeight: "96dvh",
          m: 1,
          borderRadius: "18px",
          bgcolor: color.white,
          backgroundImage: "none",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box
        sx={{
          px: { xs: 2, md: 3 },
          py: 2,
          borderBottom: `1px solid ${color.gray200}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexShrink: 0,
          bgcolor: color.white,
        }}
      >
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ minWidth: 0 }}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "12px",
              bgcolor: color.blue50,
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
            }}
          >
            <AssignmentOutlinedIcon sx={{ color: color.secondary }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                color: `${color.secondary} !important`,
                fontSize: 11,
                fontWeight: 800,
                mb: 0.3,
              }}
            >
              {ticket ? `#${ticket.id}` : t("details.title")}
            </Typography>
            <Typography
              sx={{
                color: `${color.primary_dark} !important`,
                fontSize: { xs: 16, md: 21 },
                fontWeight: 800,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {ticket?.title ?? t("details.title")}
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
          {access?.is_admin && ticket && (
            <Tooltip title={t("actions.deleteTicket")} arrow>
              <span>
                <IconButton
                  onClick={() => setConfirmDelete(true)}
                  disabled={busy}
                  aria-label={t("actions.deleteTicket")}
                  sx={{
                    bgcolor: color.gray50,
                    borderRadius: "10px",
                    "&:hover": { bgcolor: color.red50 },
                  }}
                >
                  <DeleteOutlineIcon sx={{ color: color.red700 }} />
                </IconButton>
              </span>
            </Tooltip>
          )}
          <IconButton
            onClick={close}
            disabled={busy}
            aria-label={t("actions.closeDialog")}
            sx={{
              bgcolor: color.gray50,
              borderRadius: "10px",
              "&:hover": {
                bgcolor: color.blue50,
              },
            }}
          >
            <CloseIcon sx={{ color: color.gray500 }} />
          </IconButton>
        </Stack>
      </Box>
      <ConfirmationDialog
        open={confirmLoginAsUser}
        onClose={() => setConfirmLoginAsUser(false)}
        title="Login as User"
        content={`Are you sure you want to log in as ${creatorFullName}?`}
        description=""
        actions={[
          { label: "No", onClick: () => setConfirmLoginAsUser(false) },
          {
            label: "Yes",
            onClick: () => {
              setConfirmLoginAsUser(false);
              handleLoginAsUser();
            },
          },
        ]}
      />
      <Dialog
        open={confirmDelete}
        onClose={() => !deleteMutation.isPending && setConfirmDelete(false)}
        PaperProps={{
          sx: {
            bgcolor: color.white,
            backgroundImage: "none",
            borderRadius: "14px",
          },
        }}
      >
        <DialogTitle
          sx={{ color: `${color.primary_dark} !important`, fontWeight: 800 }}
        >
          {t("deleteDialog.title")}
        </DialogTitle>
        <DialogContent>
          <Typography
            sx={{ color: `${color.primary_dark} !important`, fontSize: 14 }}
          >
            {t("deleteDialog.text", { id: ticket?.id })}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setConfirmDelete(false)}
            disabled={deleteMutation.isPending}
            sx={{
              color: `${color.primary_dark} !important`,
              textTransform: "none",
            }}
          >
            {t("actions.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={removeTicket}
            disabled={deleteMutation.isPending}
            startIcon={
              deleteMutation.isPending ? (
                <CircularProgress size={15} sx={{ color: color.white }} />
              ) : (
                <DeleteOutlineIcon />
              )
            }
            sx={{
              bgcolor: `${color.red700} !important`,
              color: `${color.white} !important`,
              textTransform: "none",
              boxShadow: "none",
            }}
          >
            {t("deleteDialog.confirm")}
          </Button>
        </DialogActions>
      </Dialog>
      {isLoading ? (
        <Box
          sx={{
            flex: 1,
            display: "grid",
            placeItems: "center",
          }}
        >
          <CircularProgress sx={{ color: color.accent }} />
        </Box>
      ) : isError ? (
        <Box sx={{ p: 3 }}>
          <Alert
            severity="error"
            action={
              <Button onClick={() => refetch()}>{t("actions.retry")}</Button>
            }
          >
            {t("details.loadError")}
          </Alert>
        </Box>
      ) : !ticket ? (
        <Box sx={{ p: 3 }}>
          <Alert severity="info">{t("tickets.notFound")}</Alert>
        </Box>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "minmax(0, 1fr)",
              md: "minmax(0, 0.9fr) minmax(0, 1.1fr)",
            },
            flex: 1,
            minHeight: 0,
            overflowY: {
              xs: "auto",
              md: "hidden",
            },
          }}
        >
          <Box
            sx={{
              p: { xs: 2, md: 3 },
              minWidth: 0,
              overflowY: {
                xs: "visible",
                md: "auto",
              },
              bgcolor: color.white,
            }}
          >
            <Stack spacing={3}>
              <Box>
                <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                  <BadgeOutlined
                    sx={{ color: color.secondary, fontSize: 20 }}
                  />
                  <Typography
                    sx={{
                      color: `${color.primary_dark} !important`,
                      fontSize: 15,
                      fontWeight: 800,
                    }}
                  >
                    {t("submittedBy")}
                  </Typography>
                </Stack>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: color.gray50,
                    border: `1px solid ${color.gray200}`,
                    borderRadius: "12px",
                  }}
                >
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1.5}
                    alignItems={{ xs: "stretch", sm: "center" }}
                  >
                    <Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                      sx={{ flex: 1, minWidth: 0 }}
                    >
                      <Avatar
                        sx={{
                          width: 40,
                          height: 40,
                          bgcolor: color.blue50,
                          color: color.secondary,
                          fontSize: 14,
                          fontWeight: 800,
                        }}
                      >
                        {creator
                          ? [creator.name?.[0], creator.surname?.[0]]
                              .filter(Boolean)
                              .join("")
                              .toUpperCase() || "?"
                          : "?"}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ ...valueSx, fontSize: 13 }}>
                          {creatorFullName || "—"}
                        </Typography>
                        {creator?.email && (
                          <Typography
                            sx={{
                              color: color.gray500,
                              fontSize: 11,
                              mt: 0.25,
                              overflowWrap: "anywhere",
                            }}
                          >
                            {creator.email}
                          </Typography>
                        )}
                        {ticket.created_at &&
                          !Number.isNaN(
                            new Date(ticket.created_at).getTime(),
                          ) && (
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={0.6}
                              sx={{ mt: 0.75 }}
                            >
                              <AccessTimeOutlined
                                sx={{ color: color.gray500, fontSize: 13 }}
                              />
                              <Typography
                                sx={{
                                  color: color.gray500,
                                  fontSize: 11,
                                  fontWeight: 500,
                                }}
                              >
                                {new Intl.DateTimeFormat(
                                  i18n.language?.startsWith("de")
                                    ? "de-DE"
                                    : "en-GB",
                                  { dateStyle: "medium", timeStyle: "short" },
                                ).format(new Date(ticket.created_at))}
                              </Typography>
                            </Stack>
                          )}
                      </Box>
                    </Stack>
                    {access?.is_admin && ticketUserId != null && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={
                          loginAsUserMutation.isPending ? (
                            <CircularProgress size={15} />
                          ) : (
                            <BadgeOutlined sx={{ fontSize: 18 }} />
                          )
                        }
                        disabled={busy}
                        onClick={() => setConfirmLoginAsUser(true)}
                        sx={{
                          textTransform: "none",
                          borderRadius: "8px",
                          borderColor: color.gray300,
                          color: color.secondary,
                          fontSize: 11,
                          fontWeight: 700,
                          minHeight: 34,
                          whiteSpace: "nowrap",
                          alignSelf: { xs: "flex-start", sm: "center" },
                          "&:hover": {
                            borderColor: color.secondary,
                            bgcolor: color.blue50,
                          },
                        }}
                      >
                        {t("loginAsUser")}
                      </Button>
                    )}
                  </Stack>
                </Box>
              </Box>
              <Box>
                <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                  <DescriptionOutlinedIcon
                    sx={{
                      color: color.secondary,
                      fontSize: 20,
                    }}
                  />
                  <Typography
                    sx={{
                      color: `${color.primary_dark} !important`,
                      fontSize: 15,
                      fontWeight: 800,
                    }}
                  >
                    {t("details.description")}
                  </Typography>
                </Stack>
                <Box
                  sx={{
                    bgcolor: color.gray50,
                    border: `1px solid ${color.gray200}`,
                    borderRadius: "12px",
                    p: 2.5,
                    minHeight: 130,
                  }}
                >
                  <Typography
                    sx={{
                      color: `${color.primary_dark} !important`,
                      fontSize: 13,
                      lineHeight: 1.8,
                      whiteSpace: "pre-wrap",
                      overflowWrap: "anywhere",
                    }}
                  >
                    {ticket.description || t("tickets.noDescription")}
                  </Typography>
                  <AttachmentList attachments={ticket.attachments ?? []} />
                </Box>
              </Box>
              <Box>
                <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                  <HistoryOutlined
                    sx={{ color: color.secondary, fontSize: 20 }}
                  />
                  <Typography
                    sx={{
                      color: `${color.primary_dark} !important`,
                      fontSize: 15,
                      fontWeight: 800,
                    }}
                  >
                    {t("admin.title")}
                  </Typography>
                </Stack>
                <Box
                  sx={{
                    bgcolor: color.gray50,
                    border: `1px solid ${color.gray200}`,
                    borderRadius: "12px",
                    p: 2,
                  }}
                >
                  {ticket.status_history?.length ? (
                    <Stack spacing={0}>
                      {ticket.status_history.map((change, index) => {
                        const fromStyle =
                          statusStyles[change.from_status] ?? statusStyles[0];
                        const toStyle =
                          statusStyles[change.to_status] ?? statusStyles[0];
                        const changedBy = change.changed_by
                          ? [change.changed_by.name, change.changed_by.surname]
                              .filter(Boolean)
                              .join(" ") || change.changed_by.email
                          : "—";
                        const parsedDate = new Date(
                          change.created_at
                            .replace(" ", "T")
                            .replace(/(\.\d{3})\d+$/, "$1"),
                        );
                        return (
                          <Box
                            key={change.id}
                            sx={{
                              display: "flex",
                              gap: 1.5,
                              pb:
                                index === ticket.status_history!.length - 1
                                  ? 0
                                  : 2,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                pt: 0.5,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 9,
                                  height: 9,
                                  borderRadius: "50%",
                                  bgcolor: color.secondary,
                                  flexShrink: 0,
                                }}
                              />
                              {index < ticket.status_history!.length - 1 && (
                                <Box
                                  sx={{
                                    width: "1px",
                                    bgcolor: color.gray300,
                                    flex: 1,
                                    mt: 0.75,
                                  }}
                                />
                              )}
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={0.75}
                                flexWrap="wrap"
                                useFlexGap
                              >
                                <Chip
                                  size="small"
                                  label={statusLabels[change.from_status]}
                                  sx={{
                                    bgcolor: fromStyle.bg,
                                    color: fromStyle.text,
                                    fontSize: 11,
                                    fontWeight: 700,
                                  }}
                                />
                                <ArrowForwardRounded
                                  sx={{ fontSize: 16, color: color.gray500 }}
                                />
                                <Chip
                                  size="small"
                                  label={statusLabels[change.to_status]}
                                  sx={{
                                    bgcolor: toStyle.bg,
                                    color: toStyle.text,
                                    fontSize: 11,
                                    fontWeight: 700,
                                  }}
                                />
                              </Stack>
                              <Typography
                                sx={{
                                  color: color.gray500,
                                  fontSize: 11,
                                  mt: 0.75,
                                  overflowWrap: "anywhere",
                                }}
                              >
                                {Number.isNaN(parsedDate.getTime())
                                  ? change.created_at
                                  : new Intl.DateTimeFormat(
                                      i18n.language?.startsWith("de")
                                        ? "de-DE"
                                        : "en-GB",
                                      {
                                        dateStyle: "medium",
                                        timeStyle: "short",
                                      },
                                    ).format(parsedDate)}
                                {` · ${changedBy}`}
                              </Typography>
                            </Box>
                          </Box>
                        );
                      })}
                    </Stack>
                  ) : (
                    <Typography sx={{ color: color.gray500, fontSize: 12 }}>
                      {t("admin.empty")}
                    </Typography>
                  )}
                </Box>
              </Box>
              <Divider sx={{ borderColor: color.gray200 }} />
              <Box>
                <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                  <LocalOfferOutlinedIcon
                    sx={{ color: color.secondary, fontSize: 20 }}
                  />
                  <Typography
                    sx={{
                      color: color.primary_dark,
                      fontSize: 15,
                      fontWeight: 800,
                    }}
                  >
                    {t("createDialog.propertiesSection")}
                  </Typography>
                </Stack>
                {canManage ? (
                  <Stack spacing={2}>
                    {updateMutation.isError && (
                      <Alert severity="error">{t("admin.accessError")}</Alert>
                    )}
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1.5}
                    >
                      <TextField
                        select
                        size="small"
                        fullWidth
                        label={t("details.status")}
                        value={ticket.status}
                        disabled={updateMutation.isPending}
                        onChange={(e) =>
                          save({
                            status: Number(e.target.value) as TicketStatus,
                          })
                        }
                        sx={{
                          ...fieldSx,
                          "& .MuiSelect-select": {
                            color: `${color.primary_dark} !important`,
                            WebkitTextFillColor: `${color.primary_dark} !important`,
                          },
                        }}
                        SelectProps={{
                          MenuProps: {
                            PaperProps: {
                              sx: {
                                bgcolor: color.white,
                                backgroundImage: "none",
                                "& .MuiMenuItem-root": {
                                  color: `${color.primary_dark} !important`,
                                  fontSize: 14,
                                  "&:hover": { bgcolor: color.gray100 },
                                  "&.Mui-selected": { bgcolor: color.blue50 },
                                  "&.Mui-selected:hover": {
                                    bgcolor: color.blue50,
                                  },
                                },
                              },
                            },
                          },
                        }}
                      >
                        <MenuItem value={0}>{t("status.submitted")}</MenuItem>
                        <MenuItem value={1}>{t("status.inProgress")}</MenuItem>
                        <MenuItem value={2}>{t("status.closed")}</MenuItem>
                      </TextField>
                      <TextField
                        select
                        size="small"
                        fullWidth
                        label={t("details.priority")}
                        value={ticket.priority}
                        disabled={updateMutation.isPending}
                        onChange={(e) =>
                          save({
                            priority: Number(e.target.value) as TicketPriority,
                          })
                        }
                        sx={{
                          ...fieldSx,
                          "& .MuiSelect-select": {
                            color: `${color.primary_dark} !important`,
                            WebkitTextFillColor: `${color.primary_dark} !important`,
                          },
                        }}
                        SelectProps={{
                          MenuProps: {
                            PaperProps: {
                              sx: {
                                bgcolor: color.white,
                                backgroundImage: "none",
                                "& .MuiMenuItem-root": {
                                  color: `${color.primary_dark} !important`,
                                  fontSize: 14,
                                  "&:hover": { bgcolor: color.gray100 },
                                  "&.Mui-selected": { bgcolor: color.blue50 },
                                  "&.Mui-selected:hover": {
                                    bgcolor: color.blue50,
                                  },
                                },
                              },
                            },
                          },
                        }}
                      >
                        <MenuItem value={0}>{t("priority.low")}</MenuItem>
                        <MenuItem value={1}>{t("priority.normal")}</MenuItem>
                        <MenuItem value={2}>{t("priority.high")}</MenuItem>
                      </TextField>
                    </Stack>
                    {infoRow(
                      t("details.category"),
                      <Typography sx={valueSx}>
                        {ticket.category || t("tickets.notSpecified")}
                      </Typography>,
                    )}
                    {infoRow(
                      t("details.project"),
                      <Typography sx={valueSx}>
                        {ticket.workspace?.name ?? t("tickets.general")}
                      </Typography>,
                    )}
                    <Divider sx={{ borderColor: color.gray200 }} />
                    <Stack spacing={1}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        mb={1.5}
                      >
                        <SellOutlined
                          sx={{ color: color.secondary, fontSize: 20 }}
                        />
                        <Typography
                          sx={{
                            color: `${color.primary_dark} !important`,
                            fontSize: 15,
                            fontWeight: 800,
                          }}
                        >
                          {t("admin.manageTags")}
                        </Typography>
                      </Stack>
                      <Stack
                        direction="row"
                        gap={0.5}
                        flexWrap="wrap"
                        useFlexGap
                      >
                        {tags.map((tag) => (
                          <Chip
                            key={tag}
                            label={`#${tag}`}
                            size="small"
                            onDelete={() =>
                              setTags((current) =>
                                current.filter((x) => x !== tag),
                              )
                            }
                            sx={{
                              bgcolor: color.orange50,
                              color: color.accent_dark,
                              height: 23,
                              fontSize: 11,
                              "& .MuiChip-deleteIcon": { fontSize: 15 },
                            }}
                          />
                        ))}
                      </Stack>
                      <Stack direction="row" spacing={1}>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder={t("admin.tagsPlaceholder")}
                          sx={fieldSx}
                          value={tagInput}
                          onChange={(e) => setTagInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              setTags((current) =>
                                Array.from(
                                  new Set([...current, ...parseTags(tagInput)]),
                                ),
                              );
                              setTagInput("");
                            }
                          }}
                        />
                        <Button
                          variant="outlined"
                          sx={{
                            color: color.secondary,
                            borderColor: color.gray300,
                            textTransform: "none",
                            height: 38,
                            minWidth: 62,
                            fontSize: 11,
                            px: 1.5,
                          }}
                          onClick={() => {
                            setTags((current) =>
                              Array.from(
                                new Set([...current, ...parseTags(tagInput)]),
                              ),
                            );
                            setTagInput("");
                          }}
                        >
                          {t("admin.add")}
                        </Button>
                      </Stack>
                      <Button
                        variant="outlined"
                        disabled={
                          updateMutation.isPending ||
                          JSON.stringify(
                            Array.from(
                              new Set([...tags, ...parseTags(tagInput)]),
                            ),
                          ) === JSON.stringify(ticket.tags ?? [])
                        }
                        onClick={() =>
                          save({
                            tags: Array.from(
                              new Set([...tags, ...parseTags(tagInput)]),
                            ),
                          })
                        }
                        sx={{
                          alignSelf: "flex-start",
                          color: color.secondary,
                          borderColor: color.gray300,
                          textTransform: "none",
                          fontSize: 11,
                          minHeight: 32,
                          px: 1.5,
                        }}
                      >
                        {updateMutation.isPending
                          ? t("admin.saving")
                          : t("admin.saveTags")}
                      </Button>
                    </Stack>
                    <Divider sx={{ borderColor: color.gray200 }} />
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      mb={1.5}
                    >
                      <TaskAltOutlined
                        sx={{ color: color.secondary, fontSize: 20 }}
                      />
                      <Typography
                        sx={{
                          color: `${color.primary_dark} !important`,
                          fontSize: 15,
                          fontWeight: 800,
                        }}
                      >
                        {t("admin.resolutionLabel")}
                      </Typography>
                    </Stack>
                    <TextField
                      multiline
                      minRows={2}
                      maxRows={5}
                      fullWidth
                      size="small"
                      sx={fieldSx}
                      value={resolution}
                      onChange={(e) => setResolution(e.target.value)}
                    />
                    <Button
                      variant="contained"
                      disabled={
                        updateMutation.isPending ||
                        !resolution.trim() ||
                        resolution.trim() === (ticket.resolution ?? "").trim()
                      }
                      onClick={() => save({ resolution: resolution.trim() })}
                      sx={{
                        alignSelf: "flex-start",
                        bgcolor: color.accent,
                        color: color.white,
                        textTransform: "none",
                        boxShadow: "none",
                        "&:hover": { bgcolor: color.accent_dark },
                      }}
                    >
                      {updateMutation.isPending
                        ? t("admin.saving")
                        : t("admin.sendResolution")}
                    </Button>
                  </Stack>
                ) : (
                  <Stack>
                    {infoRow(
                      t("details.status"),
                      <Chip
                        size="small"
                        label={statusLabels[ticket.status]}
                        sx={{
                          bgcolor: statusStyle.bg,
                          color: statusStyle.text,
                        }}
                      />,
                    )}
                    {infoRow(
                      t("details.priority"),
                      <Chip
                        size="small"
                        label={priorityLabels[ticket.priority]}
                        sx={{
                          bgcolor: priorityStyle.bg,
                          color: priorityStyle.text,
                        }}
                      />,
                    )}
                    {infoRow(
                      t("details.category"),
                      <Typography sx={valueSx}>
                        {ticket.category || t("tickets.notSpecified")}
                      </Typography>,
                    )}
                    {infoRow(
                      t("details.project"),
                      <Typography sx={valueSx}>
                        {ticket.workspace?.name ?? t("tickets.general")}
                      </Typography>,
                    )}
                  </Stack>
                )}
              </Box>
            </Stack>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              minWidth: 0,
              minHeight: {
                xs: 460,
                md: 0,
              },
              overflow: "hidden",
              bgcolor: color.gray50,
              borderLeft: {
                xs: "none",
                md: `1px solid ${color.gray200}`,
              },
              borderTop: {
                xs: `1px solid ${color.gray200}`,
                md: "none",
              },
            }}
          >
            <TicketConversation
              key={ticket.id}
              ticket={ticket}
              onSent={refetch}
            />
          </Box>
        </Box>
      )}
    </Dialog>
  );
};
export default AdminTicketEditModal;
